/**
 * Layout Engine — physically rearranges panel DOM elements for each layout preset.
 *
 * View > Layout presets define not just panel visibility but also which row
 * each panel lives in, its flex sizing, and whether panels dock full-height.
 */
import type { PanelVisibility } from '@graphicsim/shared-types';
import { initVerticalResize, initHorizontalResize } from './panelResize';

// ─── Data Structures ────────────────────────────────────────────────

interface PanelSlot {
  id: string;
  flex?: string;   // e.g. '1', '1.5', '4'
  width?: string;  // e.g. '220px', '550px' — overrides flex
}

interface LayoutArrangement {
  headerRow?: PanelSlot[];             // optional full-width row above everything
  topRow: PanelSlot[];
  bottomRow: PanelSlot[];
  topRowDirection?: 'row' | 'column';  // default 'row'; Express uses 'column'
  fullHeightLeft?: PanelSlot[];
  fullHeightRight?: PanelSlot[];
  rowFlex?: [number, number];          // [topFlex, bottomFlex]
  headerFlex?: number;                 // flex for the header row
}

// ─── Layout Definitions ─────────────────────────────────────────────

const LAYOUT_ARRANGEMENTS: Record<string, LayoutArrangement> = {
  live: {
    topRow: [
      { id: 'preview', flex: '1' },
      { id: 'program', flex: '1' },
    ],
    bottomRow: [
      { id: 'library', width: '220px' },
      { id: 'project', flex: '1' },
      { id: 'shots', flex: '1' },
      { id: 'properties', flex: '1' },
    ],
    rowFlex: [1.2, 0.8],
  },

  default: {
    topRow: [
      { id: 'preview', flex: '4' },
      { id: 'program', flex: '1' },
    ],
    bottomRow: [
      { id: 'project', flex: '2.5' },
      { id: 'properties', flex: '1.2' },
      { id: 'shots', flex: '1' },
    ],
    rowFlex: [1.2, 0.8],
  },

  compact: {
    topRow: [
      { id: 'preview', flex: '1' },
      { id: 'program', flex: '2' },
      { id: 'audio', flex: '3' },
    ],
    bottomRow: [
      { id: 'properties', flex: '1' },
      { id: 'project', flex: '3' },
      { id: 'shots', flex: '0.7' },
      { id: 'switcher', flex: '0.7' },
    ],
    rowFlex: [0.6, 1.4],
  },

  express: {
    topRow: [
      { id: 'preview', flex: '1' },
      { id: 'program', flex: '1.5' },
    ],
    topRowDirection: 'column',
    bottomRow: [
      { id: 'properties', flex: '1' },
      { id: 'shots', flex: '1' },
    ],
    fullHeightLeft: [
      { id: 'project', width: '550px' },
    ],
    fullHeightRight: [
      { id: 'switcher', width: '220px' },
    ],
    rowFlex: [2, 0.8],
  },

  scoretable: {
    topRow: [
      { id: 'program', flex: '1' },
    ],
    bottomRow: [
      { id: 'shots', flex: '1' },
    ],
    rowFlex: [1.2, 0.8],
  },

  setup: {
    headerRow: [
      { id: 'library', flex: '1' },
    ],
    headerFlex: 0.25,
    topRow: [
      { id: 'preview', flex: '1' },
      { id: 'program', flex: '1' },
    ],
    bottomRow: [
      { id: 'properties', flex: '1' },
      { id: 'shots', flex: '1' },
    ],
    fullHeightLeft: [
      { id: 'project', width: '350px' },
    ],
    rowFlex: [1, 1],
  },
};

// ─── Visibility Derivation ──────────────────────────────────────────

const ALL_PANEL_IDS: (keyof PanelVisibility)[] = [
  'library', 'shots', 'properties', 'switcher', 'audio', 'program', 'preview', 'data',
];

/**
 * Derive panel visibility from a layout arrangement.
 * Panels in the arrangement are visible; all others hidden.
 */
export function getLayoutVisibility(preset: string): PanelVisibility {
  const arrangement = LAYOUT_ARRANGEMENTS[preset];
  if (!arrangement) {
    // Fallback to live
    return getLayoutVisibility('live');
  }

  const visible = new Set<string>();
  if (arrangement.headerRow) {
    for (const slot of arrangement.headerRow) visible.add(slot.id);
  }
  for (const slot of arrangement.topRow) visible.add(slot.id);
  for (const slot of arrangement.bottomRow) visible.add(slot.id);
  if (arrangement.fullHeightLeft) {
    for (const slot of arrangement.fullHeightLeft) visible.add(slot.id);
  }
  if (arrangement.fullHeightRight) {
    for (const slot of arrangement.fullHeightRight) visible.add(slot.id);
  }

  const result: Record<string, boolean> = {};
  for (const id of ALL_PANEL_IDS) {
    result[id] = visible.has(id);
  }
  return result as PanelVisibility;
}

// ─── DOM Manipulation ───────────────────────────────────────────────

/**
 * Collect all panel elements by their data-panel-id into a Map.
 */
function collectPanels(): Map<string, HTMLElement> {
  const map = new Map<string, HTMLElement>();
  document.querySelectorAll<HTMLElement>('[data-panel-id]').forEach((el) => {
    map.set(el.dataset.panelId!, el);
  });
  return map;
}

/**
 * Remove all vertical dividers from a container element.
 */
function clearDividers(container: HTMLElement): void {
  container.querySelectorAll<HTMLElement>(':scope > .panel-divider-v').forEach((d) => {
    d.remove();
  });
}

/**
 * Remove all panels from their current DOM parents (without destroying them).
 */
function detachAllPanels(panelMap: Map<string, HTMLElement>): void {
  for (const panel of panelMap.values()) {
    panel.remove();
  }
}

/**
 * Create a vertical panel divider and init its resize handler.
 */
function createDivider(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'panel-divider-v';
  initVerticalResize(div);
  return div;
}

/**
 * Insert panels into a row element with dividers between them.
 * Applies flex or width from the PanelSlot definitions.
 */
function populateRow(row: HTMLElement, slots: PanelSlot[], panelMap: Map<string, HTMLElement>): void {
  let first = true;
  for (const slot of slots) {
    const panel = panelMap.get(slot.id);
    if (!panel) continue;

    if (!first) {
      row.appendChild(createDivider());
    }
    first = false;

    // Reset inline sizing
    panel.style.flex = '';
    panel.style.width = '';
    panel.style.height = '';

    if (slot.width) {
      panel.style.flex = `0 0 ${slot.width}`;
      panel.style.width = slot.width;
    } else if (slot.flex) {
      panel.style.flex = slot.flex;
    } else {
      panel.style.flex = '1';
    }

    // Ensure panel displays as flex column
    panel.style.display = 'flex';
    panel.style.flexDirection = 'column';

    row.appendChild(panel);
  }
}

/**
 * Insert full-height panels into a container (before or after a reference element)
 * with vertical dividers.
 */
function insertFullHeight(
  container: HTMLElement,
  refElement: HTMLElement,
  slots: PanelSlot[],
  side: 'left' | 'right',
  panelMap: Map<string, HTMLElement>,
): void {
  for (const slot of slots) {
    const panel = panelMap.get(slot.id);
    if (!panel) continue;

    panel.style.flex = 'none';
    panel.style.width = slot.width || '300px';
    panel.style.height = '';
    panel.style.display = 'flex';
    panel.style.flexDirection = 'column';

    const divider = createDivider();

    if (side === 'left') {
      container.insertBefore(panel, refElement);
      container.insertBefore(divider, refElement);
    } else {
      container.appendChild(divider);
      container.appendChild(panel);
    }
  }
}

/**
 * Park unused panels (hidden) at end of bottom row so they remain in the DOM.
 */
function parkUnused(bottomRow: HTMLElement, panelMap: Map<string, HTMLElement>, usedIds: Set<string>): void {
  for (const [id, panel] of panelMap) {
    if (!usedIds.has(id)) {
      panel.style.display = 'none';
      panel.style.flex = '';
      panel.style.width = '';
      panel.style.height = '';
      bottomRow.appendChild(panel);
    }
  }
}

// ─── Main Entry Point ───────────────────────────────────────────────

/**
 * Physically rearrange panel DOM elements to match the given layout preset.
 * This moves panels between rows, adds/removes dividers, and sets flex values.
 */
export function applyLayout(preset: string, panelVisibility?: Partial<PanelVisibility>): void {
  const arrangement = LAYOUT_ARRANGEMENTS[preset];
  if (!arrangement) return;

  const workspace = document.getElementById('workspace');
  if (!workspace) return;

  // Handle ws-body wrapper: if it exists, unwrap back to normal structure
  const existingBody = workspace.querySelector('.ws-body') as HTMLElement;
  const wsMain = (existingBody || workspace).querySelector('.ws-main') as HTMLElement;
  if (!wsMain) return;

  // If ws-body exists, move ws-main back to workspace and remove ws-body
  if (existingBody) {
    // Move all children of ws-body back into workspace (before ws-body)
    while (existingBody.firstChild) {
      workspace.insertBefore(existingBody.firstChild, existingBody);
    }
    existingBody.remove();
  }

  // Remove any existing header row — but rescue panels inside it first
  workspace.querySelectorAll(':scope > .ws-header-row').forEach((row) => {
    row.querySelectorAll<HTMLElement>('[data-panel-id]').forEach((panel) => {
      workspace.appendChild(panel); // temporarily park in workspace so collectPanels finds them
    });
    row.remove();
  });
  workspace.querySelectorAll(':scope > .panel-divider-h').forEach((el) => el.remove());

  // Reset workspace to flex-row (default)
  workspace.style.flexDirection = 'row';

  const topRow = wsMain.querySelector('.ws-row.top') as HTMLElement;
  const bottomRow = wsMain.querySelector('.ws-row.bottom') as HTMLElement;
  if (!topRow || !bottomRow) return;

  // 1. Collect all panels
  const panelMap = collectPanels();

  // 2. Detach all panels from DOM
  detachAllPanels(panelMap);

  // 3. Clear all vertical dividers from rows and workspace
  clearDividers(topRow);
  clearDividers(bottomRow);
  clearDividers(workspace);

  // Also remove any full-height panels/dividers that are direct children of workspace
  // (except .ws-main itself)
  const wsChildren = Array.from(workspace.children);
  for (const child of wsChildren) {
    if (child !== wsMain && child.classList.contains('panel-divider-v')) {
      child.remove();
    }
  }

  // 4. Reset row inline styles
  topRow.style.flex = '';
  topRow.style.height = '';
  topRow.style.flexDirection = '';
  bottomRow.style.flex = '';
  bottomRow.style.height = '';
  bottomRow.style.flexDirection = '';

  // Apply row flex ratios
  if (arrangement.rowFlex) {
    topRow.style.flex = String(arrangement.rowFlex[0]);
    bottomRow.style.flex = String(arrangement.rowFlex[1]);
  }

  // Apply top row direction (Express uses 'column' for stacking preview/program)
  if (arrangement.topRowDirection) {
    topRow.style.flexDirection = arrangement.topRowDirection;
  }

  // 5. Build set of all used panel IDs (only include header panels that pass visibility filter)
  const usedIds = new Set<string>();
  if (arrangement.headerRow) {
    for (const slot of arrangement.headerRow) {
      const visible = !panelVisibility || panelVisibility[slot.id as keyof PanelVisibility] !== false;
      if (visible) usedIds.add(slot.id);
    }
  }
  for (const slot of arrangement.topRow) usedIds.add(slot.id);
  for (const slot of arrangement.bottomRow) usedIds.add(slot.id);
  if (arrangement.fullHeightLeft) {
    for (const slot of arrangement.fullHeightLeft) usedIds.add(slot.id);
  }
  if (arrangement.fullHeightRight) {
    for (const slot of arrangement.fullHeightRight) usedIds.add(slot.id);
  }

  // 6. Check if we need a full-width header row
  let hasHeader = false;
  if (arrangement.headerRow && arrangement.headerRow.length > 0) {
    const visibleHeaderSlots = panelVisibility
      ? arrangement.headerRow.filter((s) => panelVisibility[s.id as keyof PanelVisibility] !== false)
      : arrangement.headerRow;

    if (visibleHeaderSlots.length > 0) {
      hasHeader = true;

      // Switch workspace to column layout: header on top, body (row) below
      workspace.style.flexDirection = 'column';

      // Create header row
      const headerRow = document.createElement('div');
      headerRow.className = 'ws-header-row';
      headerRow.style.flex = String(arrangement.headerFlex ?? 0.6);
      headerRow.style.display = 'flex';
      headerRow.style.minHeight = '0';
      populateRow(headerRow, visibleHeaderSlots, panelMap);
      workspace.insertBefore(headerRow, workspace.firstChild);

      // Insert horizontal divider after header (resizable)
      const hDiv = document.createElement('div');
      hDiv.className = 'panel-divider-h';
      initHorizontalResize(hDiv);
      workspace.insertBefore(hDiv, headerRow.nextSibling);

      // Wrap remaining content (ws-main + full-height panels) in ws-body flex-row
      const wsBody = document.createElement('div');
      wsBody.className = 'ws-body';
      wsBody.style.cssText = 'display:flex;flex-direction:row;flex:1;min-height:0;overflow:hidden;';

      // Move ws-main into ws-body
      wsBody.appendChild(wsMain);
      workspace.appendChild(wsBody);

      // Insert full-height left panels into ws-body (before ws-main)
      if (arrangement.fullHeightLeft) {
        insertFullHeight(wsBody, wsMain, arrangement.fullHeightLeft, 'left', panelMap);
      }

      // Insert top row panels
      populateRow(topRow, arrangement.topRow, panelMap);

      // Insert bottom row panels
      populateRow(bottomRow, arrangement.bottomRow, panelMap);

      // Insert full-height right panels into ws-body (after ws-main)
      if (arrangement.fullHeightRight) {
        insertFullHeight(wsBody, wsMain, arrangement.fullHeightRight, 'right', panelMap);
      }
    }
  }

  // 7. Standard layout (no header)
  if (!hasHeader) {
    // Insert full-height left panels
    if (arrangement.fullHeightLeft) {
      insertFullHeight(workspace, wsMain, arrangement.fullHeightLeft, 'left', panelMap);
    }

    // Insert top row panels
    populateRow(topRow, arrangement.topRow, panelMap);

    // Insert bottom row panels
    populateRow(bottomRow, arrangement.bottomRow, panelMap);

    // Insert full-height right panels
    if (arrangement.fullHeightRight) {
      insertFullHeight(workspace, wsMain, arrangement.fullHeightRight, 'right', panelMap);
    }
  }

  // 8. Park unused panels
  parkUnused(bottomRow, panelMap, usedIds);

  // 9. Fire resize event so canvases re-scale
  window.dispatchEvent(new Event('resize'));
}
